import apiService from './api';
import { API_ENDPOINTS } from '../constants';

class MachineService {
  // Get all machines with pagination and filtering
  async getMachines(params = {}) {
    try {
      const queryParams = {
        ...(params.paginate !== false && {
          page: params.page || 1,
          per_page: params.limit || params.per_page || 25
        }),
        ...(params.paginate === false && { paginate: false }),
        ...(params.sort && { sort: params.sort }),
        ...(params.order && { order: params.order }),
        ...(params.status && { status: params.status }),
        ...(params.search && { q: params.search }),
        ...(params.site !== undefined && { site: params.site }),
        ...(params.category !== undefined && { category: params.category })
      };

      const endpoint = params.search ? API_ENDPOINTS.MACHINE_SEARCH : API_ENDPOINTS.MACHINES;
      const response = await apiService.get(endpoint, queryParams);
      
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to fetch machines');
      }

      // Transform snake_case to camelCase for frontend consumption
      const transformedData = (response.data || []).map(machine => ({
        id: machine.machine_id,
        machineNumber: machine.machine_number,
        machineName: machine.machine_name,
        remarks: machine.remarks,
        engineNo: machine.engine_no || '',
        chassisNo: machine.chassis_no || '',
        purchaseDate: machine.purchase_date || '',
        insuranceNo: machine.insurance_no || '',
        insuranceDate: machine.insurance_date || '',
        pucNo: machine.puc_no || '',
        pucDate: machine.puc_date || '',
        fitnessNo: machine.fitness_no || '',
        fitnessDate: machine.fitness_date || '',
        permitNo: machine.permit_no || '',
        permitDate: machine.permit_date || '',
        nationalPermitNo: machine.national_permit_no || '',
        nationalPermitDate: machine.national_permit_date || '',
        taxNo: machine.tax_no || '',
        taxDate: machine.tax_date || '',
        site: machine.site || '',
        category: machine.category || '',
        isActive: machine.is_active,
        tenantId: machine.tenant_id,
        createdBy: machine.created_by,
        updatedBy: machine.updated_by,
        deletedBy: machine.deleted_by,
        createdAt: machine.created_at,
        updatedAt: machine.updated_at,
        deletedAt: machine.deleted_at,
        // Additional fields for UI
        associatedInventory: machine.inventory_count || 0,
        lastMaintenance: null // Will be added when maintenance tracking is implemented
      }));

      return {
        data: transformedData,
        total: params.paginate === false ? transformedData.length : (response.meta?.total || 0),
        page: params.paginate === false ? 1 : queryParams.page,
        limit: params.paginate === false ? transformedData.length : queryParams.per_page,
        totalPages: params.paginate === false ? 1 : Math.ceil((response.meta?.total || 0) / queryParams.per_page)
      };
    } catch (error) {
      console.error('Error fetching machines:', error);
      throw error;
    }
  }

  // Get machine by ID
  async getMachineById(id) {
    try {
      const response = await apiService.get(API_ENDPOINTS.MACHINE(id));
      
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to fetch machine');
      }

      // Transform snake_case to camelCase for frontend consumption
      const machine = response.data;
      const transformedMachine = {
        id: machine.machine_id,
        machineNumber: machine.machine_number,
        machineName: machine.machine_name,
        remarks: machine.remarks,
        engineNo: machine.engine_no || '',
        chassisNo: machine.chassis_no || '',
        purchaseDate: machine.purchase_date || '',
        insuranceNo: machine.insurance_no || '',
        insuranceDate: machine.insurance_date || '',
        pucNo: machine.puc_no || '',
        pucDate: machine.puc_date || '',
        fitnessNo: machine.fitness_no || '',
        fitnessDate: machine.fitness_date || '',
        permitNo: machine.permit_no || '',
        permitDate: machine.permit_date || '',
        nationalPermitNo: machine.national_permit_no || '',
        nationalPermitDate: machine.national_permit_date || '',
        taxNo: machine.tax_no || '',
        taxDate: machine.tax_date || '',
        site: machine.site || '',
        category: machine.category || '',
        isActive: machine.is_active,
        tenantId: machine.tenant_id,
        createdBy: machine.created_by,
        updatedBy: machine.updated_by,
        deletedBy: machine.deleted_by,
        createdAt: machine.created_at,
        updatedAt: machine.updated_at,
        deletedAt: machine.deleted_at,
        // Additional fields for UI
        associatedInventory: 0, // Will be calculated when inventory is implemented
        lastMaintenance: null // Will be added when maintenance tracking is implemented
      };

      return { data: transformedMachine };
    } catch (error) {
      console.error('Error fetching machine:', error);
      throw error;
    }
  }

  // Create new machine
  async createMachine(machineData) {
    try {
      const body = {
        machine_name: machineData.machineName,
        machine_number: machineData.machineNumber,
        remarks: machineData.remarks,
        engine_no: machineData.engineNo || null,
        chassis_no: machineData.chassisNo || null,
        purchase_date: machineData.purchaseDate || null,
        insurance_no: machineData.insuranceNo || null,
        insurance_date: machineData.insuranceDate || null,
        puc_no: machineData.pucNo || null,
        puc_date: machineData.pucDate || null,
        fitness_no: machineData.fitnessNo || null,
        fitness_date: machineData.fitnessDate || null,
        permit_no: machineData.permitNo || null,
        permit_date: machineData.permitDate || null,
        national_permit_no: machineData.nationalPermitNo || null,
        national_permit_date: machineData.nationalPermitDate || null,
        tax_no: machineData.taxNo || null,
        tax_date: machineData.taxDate || null,
        site: machineData.site || null,
        category: machineData.category || null,
        is_active: true // Always create machines as active by default
      };

      const response = await apiService.post(API_ENDPOINTS.MACHINES, body);
      
      if (!response?.success) {
        // Prioritize specific error details over generic message
        const errorMessage = response?.error || response?.message || 'Failed to create machine';
        throw new Error(errorMessage);
      }

      // Don't show toast here - let the component handle it
      return response;
    } catch (error) {
      console.error('Error creating machine:', error);
      // Don't show toast here - let the component handle it
      throw error;
    }
  }

  // Update machine
  async updateMachine(id, machineData) {
    try {
      const body = {};
      if (machineData.machineName !== undefined) body.machine_name = machineData.machineName;
      if (machineData.machineNumber !== undefined) body.machine_number = machineData.machineNumber;
      if (machineData.isActive !== undefined) body.is_active = machineData.isActive;
      if (machineData.remarks !== undefined) body.remarks = machineData.remarks;

      const fieldMap = {
        engineNo: 'engine_no', chassisNo: 'chassis_no', purchaseDate: 'purchase_date',
        insuranceNo: 'insurance_no', insuranceDate: 'insurance_date',
        pucNo: 'puc_no', pucDate: 'puc_date',
        fitnessNo: 'fitness_no', fitnessDate: 'fitness_date',
        permitNo: 'permit_no', permitDate: 'permit_date',
        nationalPermitNo: 'national_permit_no', nationalPermitDate: 'national_permit_date',
        taxNo: 'tax_no', taxDate: 'tax_date', site: 'site', category: 'category'
      };
      Object.entries(fieldMap).forEach(([camel, snake]) => {
        if (machineData[camel] !== undefined) body[snake] = machineData[camel];
      });

      const response = await apiService.put(API_ENDPOINTS.MACHINE(id), body);
      
      if (!response?.success) {
        // Prioritize specific error details over generic message
        const errorMessage = response?.error || response?.message || 'Failed to update machine';
        throw new Error(errorMessage);
      }

      // Don't show toast here - let the component handle it
      return response;
    } catch (error) {
      console.error('Error updating machine:', error);
      // Don't show toast here - let the component handle it
      throw error;
    }
  }

  // Delete machine
  async deleteMachine(id) {
    try {
      const response = await apiService.delete(API_ENDPOINTS.MACHINE(id));
      
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to delete machine');
      }

      // Don't show toast here - let the component handle it
      return response;
    } catch (error) {
      console.error('Error deleting machine:', error);
      // Don't show toast here - let the component handle it
      throw error;
    }
  }
}

export const machineService = new MachineService();